import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';

// ============================================================================
// TYPES
// ============================================================================

interface TimePickerProps {
  /** Time value in "HH:mm" format */
  value: string;
  /** Callback when time changes */
  onChange: (value: string) => void;
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const HOURS = Array.from({ length: 24 }, (_, i) => i);
// Paso de 1 minuto — permite valores como 23:59 (cierre a medianoche
// sin cruzar día) o cualquier hora exacta.
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

// ============================================================================
// COMPONENT
// ============================================================================

export function TimePicker({ value, onChange, className }: TimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const [selectedHour, selectedMinute] = useMemo(() => {
    const parts = value.split(':').map(Number);
    return [parts[0] || 0, parts[1] || 0];
  }, [value]);

  const formattedTime = `${String(selectedHour).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}`;

  // Calculate position relative to trigger
  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= dropdownHeight
        ? rect.bottom + 4
        : rect.top - dropdownHeight - 4;
      setPosition({ top, left: rect.left });
    }
    setIsOpen(true);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Scroll to selected values on open
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      if (hoursRef.current) {
        const item = hoursRef.current.children[selectedHour] as HTMLElement;
        if (item) item.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
      if (minutesRef.current) {
        const item = minutesRef.current.children[selectedMinute] as HTMLElement;
        if (item) item.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    });
  }, [isOpen, selectedHour, selectedMinute]);

  const handleHourSelect = (h: number) => {
    onChange(`${String(h).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}`);
  };

  const handleMinuteSelect = (m: number) => {
    onChange(`${String(selectedHour).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => isOpen ? setIsOpen(false) : openDropdown()}
        className={cn(
          'flex h-10 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
          'bg-background border-border text-foreground',
          'hover:border-foreground-tertiary',
          isOpen && 'ring-2 ring-ring/20 border-foreground-tertiary',
          className
        )}
      >
        {/* Clock icon */}
        <svg
          className="h-4 w-4 text-muted-foreground shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        <span className="font-medium tabular-nums">{formattedTime}</span>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 9999 }}
          className="rounded-lg border border-border bg-background shadow-lg overflow-hidden animate-in fade-in-0 zoom-in-95 duration-100"
        >
          {/* Column headers */}
          <div className="flex border-b border-border bg-background-secondary">
            <div className="w-16 py-2 text-center text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              Hr
            </div>
            <div className="w-16 py-2 text-center text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-l border-border">
              Min
            </div>
          </div>

          <div className="flex">
            {/* Hours column */}
            <div ref={hoursRef} className="h-52 w-16 overflow-y-auto">
              {HOURS.map(h => (
                <button
                  key={h}
                  type="button"
                  onClick={() => handleHourSelect(h)}
                  className={cn(
                    'w-full py-1.5 text-sm text-center transition-colors',
                    h === selectedHour
                      ? 'bg-primary text-primary-foreground font-semibold'
                      : 'text-foreground hover:bg-muted'
                  )}
                >
                  {String(h).padStart(2, '0')}
                </button>
              ))}
            </div>

            {/* Minutes column */}
            <div ref={minutesRef} className="h-52 w-16 overflow-y-auto border-l border-border">
              {MINUTES.map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => handleMinuteSelect(m)}
                  className={cn(
                    'w-full py-1.5 text-sm text-center transition-colors',
                    m === selectedMinute
                      ? 'bg-primary text-primary-foreground font-semibold'
                      : 'text-foreground hover:bg-muted'
                  )}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
